import { Button, Form, Input } from "antd";
import type { InputRef } from "antd";
import type { MessageInstance } from "antd/es/message/interface";
import { useEffect, useRef, useState } from "react";
import { loginWithPassword } from "../../../../apis/auth.api";
import { AuthError } from "../../../../apis/auth.errors";
import { establishSession } from "../../../../apis/session";
import { applyFieldErrors } from "../apply_field_errors";

interface Props {
  initialPhone: string;
  messageApi: MessageInstance;
  onNeedsPasswordSetup: (phone: string) => void;
  onForgotPassword: (phone: string) => void;
}

interface Values {
  phone: string;
  password: string;
}

const PasswordLoginForm = ({
  initialPhone,
  messageApi,
  onNeedsPasswordSetup,
  onForgotPassword,
}: Props) => {
  const [form] = Form.useForm<Values>();
  const [submitting, setSubmitting] = useState(false);
  const phoneRef = useRef<InputRef>(null);

  useEffect(() => {
    phoneRef.current?.focus();
  }, []);

  const onFinish = async ({ phone, password }: Values) => {
    const contact = (phone ?? "").trim();
    setSubmitting(true);

    const res = await loginWithPassword(contact, password);

    if (res.ok && res.token) {
      establishSession({ token: res.token, refresh: res.refresh });
      // Deliberately leave `submitting` true: the page is navigating away, and
      // dropping the spinner first flashes the button back to idle and invites a
      // second click.
      return;
    }

    setSubmitting(false);
    const err = res as AuthError;

    // The migration path. Almost nobody has a password on day one, so this is
    // the branch most first-time users hit.
    if (err.code === "password_not_set") {
      onNeedsPasswordSetup(contact);
      return;
    }

    const orphans = applyFieldErrors(form, err, ["phone", "password"]);

    if (err.code === "invalid_credentials") {
      // On the field rather than a toast: it is announced by the form's
      // role="alert", sits next to the input it refers to, and survives until
      // the user starts fixing it.
      form.setFields([{ name: "password", errors: [err.message] }]);
    } else if (orphans.length) {
      messageApi.error(orphans.join(" "));
    } else if (!err.fieldErrors) {
      messageApi.error(err.message);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ phone: initialPhone }}
      disabled={submitting}
      requiredMark={false}
      className="w-75"
    >
      <Form.Item
        name="phone"
        label="Phone number"
        rules={[
          {
            required: true,
            // Indian mobiles start 6-9. No `g` flag: it makes the literal
            // stateful via lastIndex if it is ever reused outside AntD.
            pattern: /^[6-9]\d{9}$/,
            message: "Enter a valid 10-digit mobile number",
          },
        ]}
      >
        {/* Not <InputNumber>: it round-trips through Number, so it strips
            leading zeros, accepts "1e9", and exposes no autoComplete. Not
            type="number" either — the mouse wheel silently edits the value.
            autoComplete="username" rather than "tel" because this IS the
            account identifier; "tel" alone often fails to pair the credential
            in Chrome and 1Password. */}
        <Input
          ref={phoneRef}
          inputMode="numeric"
          maxLength={10}
          autoComplete="username"
          placeholder="10-digit mobile number"
        />
      </Form.Item>

      <Form.Item
        name="password"
        label="Password"
        rules={[{ required: true, message: "Enter your password" }]}
      >
        <Input.Password
          autoComplete="current-password"
          placeholder="Password"
        />
      </Form.Item>

      {/* Always enabled. A validity-watching disabled button would wipe the
          server errors injected above on every keystroke, explains nothing when
          it is greyed out, and can be left permanently dead by autofill. */}
      <Button type="primary" htmlType="submit" loading={submitting} block>
        <span className="fw-bold">Log in</span>
      </Button>

      <Button
        type="link"
        className="px-0 mt-2"
        onClick={() =>
          onForgotPassword(((form.getFieldValue("phone") as string) ?? "").trim())
        }
      >
        Set or reset password
      </Button>
    </Form>
  );
};

export default PasswordLoginForm;
